<template>
  <v-card class="mb-4">
    <v-card-title class="d-flex align-center justify-space-between" style="height: 48px;">
      {{ label }}
      <v-switch
        v-if="initialCountWasPositive"
        class="ma-0 pa-0 custom-switch"
        v-model="localSelected"
        @update:model-value="handleSwitchChange"
        hide-details
        density="compact"
        color="secondary"
      ></v-switch>
    </v-card-title>
    <v-card-text class="text-h3">
      {{ displayCount }}
    </v-card-text>
  </v-card>
</template>

<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
  label: {
    type: String,
    required: true
  },
  count: {
    type: Number,
    required: true
  },
  selected: {
    type: Boolean,
    default: false
  },
  identifier: {
    type: String,
    required: true
  }
});

const emit = defineEmits(['update:selected', 'filterChange']);

const localSelected = ref(props.selected);
const displayCount = ref(props.count);
const intervalId = ref(null);
const initialCountWasPositive = ref(props.count > 0);

// Function to animate the count
const animateCount = (start, end) => {
  // Clear any existing animation
  if (intervalId.value) {
    clearInterval(intervalId.value);
  }
  
  // Calculate step size and animation parameters
  const difference = end - start;
  const duration = 500; // animation duration in ms
  const framesPerSecond = 30;
  const totalFrames = duration / (500 / framesPerSecond);
  
  // Determine appropriate step size based on the difference
  // For small differences (less than 20), use smaller steps for smoother animation
  let step;
  if (Math.abs(difference) < 20) {
    step = Math.max(1, Math.abs(Math.ceil(difference / totalFrames)));
  } else {
    // For larger differences, use larger steps to make the animation more visible
    step = Math.max(1, Math.abs(Math.ceil(difference / 20)));
  }
  
  // Set direction based on whether we're counting up or down
  if (difference < 0) {
    step = -step;
  }
  
  let current = start;
  displayCount.value = current;
  
  intervalId.value = setInterval(() => {
    // Add step to current value
    current += step;
    
    // Check if we've reached or passed the target
    if ((step > 0 && current >= end) || (step < 0 && current <= end)) {
      displayCount.value = end;
      clearInterval(intervalId.value);
    } else {
      displayCount.value = current;
    }
  }, 1000 / framesPerSecond);
};

// Watch for changes in the count prop with immediate option
watch(() => props.count, (newCount, oldCount) => {
  // Track if count was ever positive (so switch stays visible)
  if (newCount > 0) {
    initialCountWasPositive.value = true;
  }

  // First time the watcher runs (immediate execution)
  if (oldCount === undefined) {
    displayCount.value = newCount;
    return;
  }

  // No change in value
  if (newCount === oldCount) {
    return;
  }

  // Animate all changes after initial load
  animateCount(displayCount.value, newCount);
}, { immediate: true });

watch(() => props.selected, (newValue) => {
  localSelected.value = newValue;
});

const handleSwitchChange = () => {
  emit('update:selected', localSelected.value);
  emit('filterChange', {
    id: props.identifier,
    selected: localSelected.value
  });
};
</script>

<style scoped>
.custom-switch {
  /* Remove any default margins/padding from the switch */
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

/* Target the internal switch component to reduce its height */
:deep(.v-switch__track),
:deep(.v-switch__thumb) {
  transform: scale(0.8);
}
</style>